/**
 * Flag-aware subscription gating selectors.
 *
 * Every selector branches internally on USE_SURVEY_BILLING:
 *  - flag ON  → reads survey-api data in `state.subscription`
 *  - flag OFF → reads the legacy project fields in `state.dashboard.project`
 * so gating call-sites don't need to know which billing system is live.
 *
 * While the survey-api status is still loading (`statusLoaded === false`) the
 * selectors return permissive values (treat as active) to avoid a gating
 * flash before the fetch resolves — legacy pages behave the same way before
 * `fetchUserProject` completes.
 */
import { USE_SURVEY_BILLING } from '../../utils/constants';
import { getActiveFormsCount } from '../Stats/selectors';

const sub = state => state.subscription || {};
const subStatus = state => sub(state).status || {};
const statusLoaded = state => sub(state).statusLoaded === true;
const project = state => (state.dashboard || {}).project || {};

export const getSubscriptionStatus = state => sub(state).status;
export const getSubscriptionUsage = state => sub(state).usage || [];
export const getPlans = state => sub(state).plans || [];
export const getPlansLoading = state => sub(state).plansLoading === true;
export const isStatusLoaded = statusLoaded;

// Subscription status is `null` once loaded for fresh users with no
// subscription — the backend auto-assigns them a default plan on their next
// gated request, so null is treated as active, not locked out.
export const hasPaidAccess = state => {
  if (USE_SURVEY_BILLING) {
    if (!statusLoaded(state)) return true;
    const s = sub(state).status;
    return !s || s.status === 'ACTIVE';
  }
  return project(state).account_type === 'paid';
};

export const isSuspended = state => {
  if (USE_SURVEY_BILLING) {
    if (!statusLoaded(state)) return false;
    return subStatus(state).status === 'SUSPENDED';
  }
  return project(state).suspended === 'YES';
};

export const isExpired = state => {
  if (USE_SURVEY_BILLING) {
    if (!statusLoaded(state)) return false;
    return subStatus(state).status === 'EXPIRED';
  }
  return project(state).max_form_expiry === 'YES';
};

// survey-api has no trial concept; legacy trial banners simply never show
// when the flag is on.
export const isTrial = state =>
  USE_SURVEY_BILLING ? false : project(state).trial === 'YES';

export const getExpiryDate = state =>
  USE_SURVEY_BILLING
    ? subStatus(state).endDate
    : project(state).expiry_date;

// featureKey: 'FORMS' | 'RESPONSES' | 'STORAGE_MB'
export const hasFeature = (state, featureKey) => {
  if (!USE_SURVEY_BILLING) return true;
  const row = getSubscriptionUsage(state).find(u => u.feature === featureKey);
  return !row || row.unlimited || row.remaining > 0;
};

const formsUsageRow = state =>
  getSubscriptionUsage(state).find(u => u.feature === 'FORMS');

// Publish gate: the plan's FORMS limit lives in usage, but the count of forms
// currently live comes from the stats endpoint (active_forms = status 'A'), not
// usage.used. Permissive when billing is off, on unlimited plans, and until the
// stats summary has loaded (avoids a false block on a cold dashboard).
export const canPublishForm = state => {
  if (!USE_SURVEY_BILLING) return true;
  const row = formsUsageRow(state);
  if (!row || row.unlimited) return true;
  const active = getActiveFormsCount(state);
  if (active === null) return true;
  return active < row.limit;
};

// Shaped for the RENEW_SUBSCRIPTION modal's `quota` prop, which keys off
// `featureKey` and displays `limit`.
export const getFormsQuota = state => {
  const row = formsUsageRow(state);
  return (
    row && { featureKey: 'FORMS', limit: row.limit, used: getActiveFormsCount(state) }
  );
};

// Legacy pages open UNPAID_BILL; the survey-api flow opens the renew modal.
// Keeps call sites to a single `showModal(renewModalName(), ...)` line.
export const renewModalName = () =>
  USE_SURVEY_BILLING ? 'RENEW_SUBSCRIPTION' : 'UNPAID_BILL';

// Gate for report/map pages that block with the renew modal. Legacy pages
// allow access only when the project is explicitly not suspended
// (`suspended === 'NO'`); flag-on requires an active survey-api subscription.
export const canViewPaidFeatures = state =>
  USE_SURVEY_BILLING
    ? hasPaidAccess(state)
    : project(state).suspended === 'NO';

/**
 * Maps survey-api payments to rows for the Settings > Billing table.
 * Payment fields (GET /payments): id, plan, currency, amount, provider,
 * method, status ('PENDING'|'PROCESSING'|'SUCCESSFUL'|'FAILED'|'CANCELLED'|
 * 'REFUNDED'|'ABANDONED'), billingCycle, reference, createdAt.
 */
export const getPaymentRows = state =>
  (sub(state).payments || []).map(payment => ({
    key: payment.id,
    date: payment.createdAt,
    reference: payment.reference,
    plan: payment.plan,
    amount: payment.amount,
    currency: payment.currency,
    status: (payment.status || '').toLowerCase(),
  }));
